<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div
        v-if="modelValue"
        class="modal-overlay"
        @click.self="close"
      >
        <div
          class="modal-container"
          :class="[
            sizeClass,
            'sm:mx-auto sm:my-auto mx-4 my-4 max-h-[calc(100vh-32px)] sm:max-h-[calc(100vh-64px)]'
          ]"
        >
          <div class="modal-header">
            <slot name="header">
              <h3>{{ title }}</h3>
            </slot>
            <button
              class="modal-close-button"
              @click="close"
            >
              <span class="sr-only">Close</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line
                  x1="18"
                  y1="6"
                  x2="6"
                  y2="18"
                />
                <line
                  x1="6"
                  y1="6"
                  x2="18"
                  y2="18"
                />
              </svg>
            </button>
          </div>
          <div class="modal-body">
            <slot />
          </div>
          <template v-if="$slots.footer">
            <div class="modal-footer">
              <slot name="footer" />
            </div>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, watch, onBeforeUnmount } from 'vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: 'Modal Title'
  },
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md', 'lg', 'xl', '2xl', 'full'].includes(value)
  }
})
const emit = defineEmits(['update:modelValue', 'close'])
const close = () => {
  emit('update:modelValue', false)
  emit('close')
}
const sizeClass = computed(() => `modal-${props.size}`)
const toggleBodyScroll = (shouldLock) => {
  if (typeof document === 'undefined' || !document.body) return
  if (shouldLock) {
    document.body.style.top = `-${window.scrollY}px`
    document.body.style.position = 'fixed'
    document.body.style.width = '100%'
    document.body.style.overflow = 'hidden'
  } else {
    const scrollY = document.body.style.top
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''
    document.body.style.overflow = ''
    window.scrollTo(0, parseInt(scrollY || '0') * -1)
  }
}
const handleEscapeKey = (event) => {
  if (event.key === 'Escape' && props.modelValue) {
    close()
  }
}
watch(
  () => props.modelValue,
  (newValue) => {
    if (import.meta.client) {
      toggleBodyScroll(newValue)
      if (newValue) {
        document.addEventListener('keydown', handleEscapeKey)
      } else {
        document.removeEventListener('keydown', handleEscapeKey)
      }
    }
  },
  { immediate: true }
)
onBeforeUnmount(() => {
  if (import.meta.client) {
    if (props.modelValue) {
      toggleBodyScroll(false)
    }
    document.removeEventListener('keydown', handleEscapeKey)
  }
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-container {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  width: calc(100% - 32px);
  position: relative;
}

@media (min-width: 640px) {
  .modal-container {
    width: auto;
  }
}

.modal-sm {
  width: 300px;
}

.modal-md {
  width: 500px;
  max-width: calc(100vw - 32px);
}

.modal-lg {
  width: 700px;
  max-width: calc(100vw - 32px);
}

.modal-xl {
  width: 900px;
  max-width: calc(100vw - 32px);
}

.modal-2xl {
  width: 1200px;
  max-width: 95vw;
}

.modal-full {
  width: 95%;
  height: 90%;
}

.modal-header {
  padding: 1rem;
  border-bottom: 1px solid rgba(139, 92, 246, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(12px) saturate(180%);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.modal-body {
  padding: 0;
  flex: 1;
  overflow-y: auto;
}

.modal-footer {
  padding: 1rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
}

.modal-close-button {
  background: transparent;
  border: none;
  cursor: pointer;
  color: #4a5568;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border-radius: 0.25rem;
}

.modal-close-button:hover {
  background-color: #f7fafc;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
